<script setup>
import { ref, computed, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  debut:             { type: String,  default: '' },
  fin:               { type: String,  default: '' },
  equipements_count: { type: Number,  default: 0 },
  rapports_count:    { type: Number,  default: 0 },
  can_see_costs:     { type: Boolean, default: false },
  data:              { type: Array,   default: () => [] },
  export_url:        { type: String,  default: '' },
  equipements_url:   { type: String,  default: '/equipements' },
})

// ── Formulaire ───────────────────────────────────────────────
const formDebut = ref(props.debut)
const formFin   = ref(props.fin)

function rechercher() {
  router.get('/equipements/compilation', { debut: formDebut.value, fin: formFin.value }, { preserveScroll: true })
}

const hasData = computed(() => props.debut && props.fin)

// ── Filtre + tri + pagination ────────────────────────────────
const search      = ref('')
const pageSize    = ref(25)
const currentPage = ref(1)
const sortKey     = ref('code')
const sortDir     = ref('asc')

watch([search, pageSize, sortKey, sortDir], () => { currentPage.value = 1 })

function toggleSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDir.value = 'asc'
  }
}

const filtered = computed(() => {
  let list = props.data
  if (search.value.trim()) {
    const q = search.value.toLowerCase()
    list = list.filter(e =>
      e.code?.toLowerCase().includes(q) ||
      e.nom?.toLowerCase().includes(q) ||
      e.description?.toLowerCase().includes(q)
    )
  }
  return [...list].sort((a, b) => {
    let aVal = a[sortKey.value] ?? ''
    let bVal = b[sortKey.value] ?? ''
    if (typeof aVal === 'string') aVal = aVal.toLowerCase()
    if (typeof bVal === 'string') bVal = bVal.toLowerCase()
    if (aVal < bVal) return sortDir.value === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDir.value === 'asc' ? 1 : -1
    return 0
  })
})

const totalPages  = computed(() => Math.max(1, Math.ceil(filtered.value.length / pageSize.value)))
const paginated   = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return filtered.value.slice(start, start + pageSize.value)
})
const pageNumbers = computed(() => {
  const pages = []
  for (let i = 1; i <= totalPages.value; i++) pages.push(i)
  return pages
})

const totalQte = computed(() => filtered.value.reduce((s, e) => s + (Number(e.quantite) || 0), 0))

function fmt(val) {
  if (val === null || val === undefined || val === '') return '—'
  return Number(val).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function detailUrl(e) {
  return `/compilations/equipement?id=${e.equipement_id}&debut=${props.debut}&fin=${props.fin}`
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        Équipements &rsaquo; Compilation
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-6xl mx-auto space-y-6">

        <!-- En-tête -->
        <div class="flex items-center justify-between">
          <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase">Compilation des équipements</h1>
          <a :href="equipements_url"
             class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all">
            <span class="material-symbols-outlined text-base">arrow_back</span>
            Retour
          </a>
        </div>

        <!-- ── Ligne du haut : Recherche + Résumé côte à côte ── -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">

          <!-- Formulaire période -->
          <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
              <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Recherche</h2>
            </div>
            <div class="p-6">
              <div class="flex items-end gap-4">
                <div class="flex-1">
                  <label class="block text-[10px] font-black uppercase tracking-widest text-outline mb-1.5">Début</label>
                  <input v-model="formDebut" type="date"
                         class="w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
                </div>
                <div class="flex-1">
                  <label class="block text-[10px] font-black uppercase tracking-widest text-outline mb-1.5">Fin</label>
                  <input v-model="formFin" type="date"
                         class="w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
                </div>
                <button @click="rechercher"
                        class="machined-sheen px-5 py-2.5 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg shrink-0">
                  <span class="material-symbols-outlined text-sm">search</span>
                  Compiler
                </button>
              </div>
            </div>
          </div>

          <!-- Résumé -->
          <div v-if="hasData" class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
              <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Résumé</h2>
            </div>
            <div class="divide-y divide-outline-variant/10">
              <div class="px-6 py-3 flex justify-between items-center">
                <span class="text-[10px] font-black uppercase tracking-widest text-outline">Période</span>
                <span class="text-xs text-on-surface font-semibold">{{ debut }} → {{ fin }}</span>
              </div>
              <div class="px-6 py-3 flex justify-between items-center">
                <span class="text-[10px] font-black uppercase tracking-widest text-outline">Équipements</span>
                <span class="text-sm font-black text-on-surface">{{ equipements_count }}</span>
              </div>
              <div class="px-6 py-3 flex justify-between items-center">
                <span class="text-[10px] font-black uppercase tracking-widest text-outline">Rapports</span>
                <span class="text-sm font-black text-on-surface">{{ rapports_count }}</span>
              </div>
            </div>
          </div>

          <!-- Placeholder si pas encore de recherche -->
          <div v-else class="bg-surface-container rounded-xl border border-outline-variant/10 border-dashed flex items-center justify-center p-8">
            <p class="text-[10px] font-black uppercase tracking-widest text-outline/40">Lancez une compilation pour voir le résumé</p>
          </div>

        </div>

        <!-- ── Résultats ── -->
        <div v-if="can_see_costs" class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">

          <!-- Header tableau -->
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex flex-wrap items-center gap-3 justify-between">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">
              Résultats <span class="text-outline/50">({{ filtered.length }})</span>
            </h2>
            <div class="flex items-center gap-3">
              <div class="relative">
                <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline text-sm pointer-events-none">search</span>
                <input v-model="search" type="text" placeholder="Filtrer..."
                       class="bg-surface-container-highest border-none rounded-md pl-9 pr-3 py-2 text-xs text-on-surface placeholder-outline focus:ring-2 focus:ring-primary/20 outline-none w-44 transition-all" />
              </div>
              <div class="flex items-center gap-2 bg-surface-container-low px-3 py-2 rounded-md border border-outline-variant/10">
                <span class="text-[10px] font-black uppercase tracking-widest text-outline">Afficher</span>
                <select v-model="pageSize"
                        class="bg-transparent border-none text-xs font-black text-primary p-0 focus:ring-0 cursor-pointer uppercase outline-none">
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                </select>
              </div>
              <a v-if="data.length > 0" :href="export_url"
                 class="flex items-center gap-2 px-3 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all">
                <span class="material-symbols-outlined text-base">download</span>
                Excel
              </a>
            </div>
          </div>

          <!-- Tableau -->
          <div class="overflow-x-auto">
            <table class="w-full text-left border-collapse">
              <thead>
                <tr class="bg-surface-container-high/60 border-b border-outline-variant/15">
                  <th v-for="col in [
                    { key: 'code',        label: 'Code',        cls: 'w-28' },
                    { key: 'nom',         label: 'Nom',         cls: '' },
                    { key: 'description', label: 'Description', cls: '' },
                    { key: 'quantite',    label: 'Quantité',    cls: 'w-32 text-right' },
                  ]" :key="col.label"
                    :class="['px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline cursor-pointer select-none hover:text-on-surface transition-colors', col.cls]"
                    @click="toggleSort(col.key)"
                  >
                    <span class="inline-flex items-center gap-1">
                      {{ col.label }}
                      <span class="material-symbols-outlined text-xs"
                            :class="sortKey === col.key ? 'text-primary' : 'text-outline/40'">
                        {{ sortKey === col.key && sortDir === 'desc' ? 'arrow_downward' : 'arrow_upward' }}
                      </span>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="(e, i) in paginated" :key="e.equipement_id"
                    :class="['hover:bg-surface-container-highest/20 transition-colors', i % 2 === 1 ? 'bg-surface-container-low/20' : '']">
                  <td class="px-6 py-3">
                    <a :href="detailUrl(e)" class="font-mono text-sm font-bold text-primary hover:brightness-125 transition-all">{{ e.code }}</a>
                  </td>
                  <td class="px-6 py-3 text-sm font-semibold text-on-surface">{{ e.nom }}</td>
                  <td class="px-6 py-3 text-sm text-on-surface/80">{{ e.description || '—' }}</td>
                  <td class="px-6 py-3 text-sm text-right font-mono text-on-surface">{{ fmt(e.quantite) }}</td>
                </tr>

                <!-- Empty state -->
                <tr v-if="paginated.length === 0">
                  <td colspan="4" class="px-6 py-16 text-center text-outline text-sm">
                    <span class="material-symbols-outlined text-4xl block mb-3 text-outline/40">construction</span>
                    Aucun résultat{{ search ? ` pour "${search}"` : ' pour cette période' }}.
                  </td>
                </tr>
              </tbody>

              <!-- Totaux -->
              <tfoot v-if="filtered.length > 0">
                <tr class="border-t-2 border-outline-variant/30 bg-surface-container-high/30">
                  <td colspan="3" class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">
                    Total ({{ filtered.length }} équipement{{ filtered.length > 1 ? 's' : '' }})
                  </td>
                  <td class="px-6 py-3 text-sm text-right font-mono font-bold text-on-surface">{{ fmt(totalQte) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- Pagination -->
          <div class="px-6 py-4 bg-surface-container-high/40 border-t border-outline-variant/15 flex justify-between items-center">
            <div class="text-[10px] text-outline font-black uppercase tracking-widest">
              Affichage de {{ filtered.length === 0 ? 0 : (currentPage - 1) * pageSize + 1 }}
              à {{ Math.min(currentPage * pageSize, filtered.length) }}
              sur {{ filtered.length }} équipement{{ filtered.length > 1 ? 's' : '' }}
            </div>
            <div class="flex gap-1">
              <button :disabled="currentPage === 1" @click="currentPage--"
                      class="w-8 h-8 flex items-center justify-center rounded bg-surface-container border border-outline-variant/15 text-outline hover:text-primary disabled:text-outline/40 transition-colors">
                <span class="material-symbols-outlined text-base">chevron_left</span>
              </button>
              <template v-for="p in pageNumbers" :key="p">
                <button
                  v-if="p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1"
                  :class="['w-8 h-8 flex items-center justify-center rounded text-[10px] font-black transition-colors',
                    p === currentPage ? 'bg-primary text-on-primary' : 'bg-surface-container-low border border-outline-variant/10 text-outline hover:text-primary']"
                  @click="currentPage = p"
                >{{ p }}</button>
                <span v-else-if="p === 2 || p === totalPages - 1"
                      class="w-8 h-8 flex items-center justify-center text-outline text-sm">…</span>
              </template>
              <button :disabled="currentPage === totalPages" @click="currentPage++"
                      class="w-8 h-8 flex items-center justify-center rounded bg-surface-container border border-outline-variant/15 text-outline hover:text-primary disabled:text-outline/40 transition-colors">
                <span class="material-symbols-outlined text-base">chevron_right</span>
              </button>
            </div>
          </div>

        </div>

        <!-- Accès refusé -->
        <div v-else-if="hasData" class="bg-surface-container rounded-xl border border-outline-variant/10 p-10 text-center">
          <span class="material-symbols-outlined text-4xl block mb-3 text-outline/40">lock</span>
          <p class="text-sm text-outline">Vous n'avez pas accès aux résultats de compilation.</p>
        </div>

        <!-- Footer -->
        <footer class="pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
